import { getDeviceCommonName, isModelVirtualWeatherStation } from '@weenat/client/dist/core/devices'
import isIrrigationDevice from '@weenat/client/dist/core/devices/isIrrigationDevice'
import { useMetricProvider } from '@weenat/client/dist/core/metrics'
import {
  computeHasNoDataForHorizon,
  getMetricLastUpdateFromSummary
} from '@weenat/client/dist/core/summary'
import { UniverseMetric } from '@weenat/client/dist/core/universes'
import { isFrost, isSoilSensorMetric } from '@weenat/client/dist/enums/MetricIds'
import { DeviceSummary } from '@weenat/client/dist/resources/measurements.type'
import { useIntl } from '@weenat/wintl'
import DelimitedFlex from 'app/src/kit/DelimitedFlex'
import Text from 'app/src/kit/Text'
import { useToggleFeature } from 'app/state'
import isNil from 'lodash-es/isNil'
import { useCallback } from 'react'
import { useTheme } from 'styled-components'
import { useBackgroundMapContext } from '../dashboard/components/DashboardMap/contexts/BackgroundMapContext'
import { PlotCardFooter } from '../dashboard/components/DashboardMap/PlotCard'
import PlotListItemMetricSummary from '../dashboard/components/DashboardMap/plotListItem/PlotListItemMetricSummary'
import { useIsAppAccessRestricted } from '../hooks/useIsAppAccessRestricted'
import Card from '../kit/Card'
import NoEmissionEmptyState from '../kit/empty-state/NoEmissionEmptyState'
import Icons from '../kit/Icons'
import Link from '../kit/LinkComponent'
import { fromLngLatArrayToObject } from '../map/utils'

interface FocusedDeviceSummaryCardProps {
  focusedMetric: UniverseMetric
  focusedHorizon: number | null
  summary: DeviceSummary
  onClose: () => void
}

/**
 * @deprecated Use SummaryCard instead. Remove this component when the `marker_redesign` feature flag is removed.
 */
const FocusedDeviceSummaryCard: FC<FocusedDeviceSummaryCardProps> = ({
  onClose,
  summary,
  focusedMetric,
  focusedHorizon
}) => {
  const { colors } = useTheme()
  const { t } = useIntl()
  const [{ marker_redesign, smv }] = useToggleFeature()

  const {
    api: { map, maps }
  } = useBackgroundMapContext()

  const { isRestricted, reason } = useIsAppAccessRestricted()

  const hasNoDataForHorizon = computeHasNoDataForHorizon({
    metric: focusedMetric,
    horizonId: focusedHorizon,
    summary: summary
  })

  const lastUpdate = getMetricLastUpdateFromSummary({
    summary,
    metric: focusedMetric,
    // No need to take tensiometers into account here since we don't have an irrigation devices yet
    horizonId: null
  })

  const {
    warningThreshold,
    name: providerName,
    lastUpdateThresholds,
    isVirtualDevice
  } = useMetricProvider({
    resource: summary.metadata.device,
    metric: focusedMetric,
    // since we won't have any virtual devices here
    isExpertOrg: false,
    smv
  })

  const commonName = getDeviceCommonName(summary.metadata.device)
  const technicalName = summary.metadata.device.endUserName?.join(' ')
  const completeName = `${commonName} (${technicalName})`

  const focusDevice = useCallback(() => {
    const { location } = summary.metadata.device

    if (!isNil(location) && location.coordinates.length >= 2 && !isNil(map) && !isNil(maps)) {
      const latLng = fromLngLatArrayToObject(location.coordinates)

      map.fitBounds(new maps.LatLngBounds(latLng, latLng), 90)
      map.setZoom(16)
    }
  }, [summary.metadata.device, map, maps])

  return (
    <Card $p={0} $width={352} $pointerEvents='auto'>
      <DelimitedFlex $p='md' $alignItems={'center'} $gap='md'>
        <Flex $flexDirection='column' $minWidth={0} $flex={1}>
          <Text $fontWeight='bold' $ellipsis>
            {!isNil(summary.metadata.device.reverseGeocoding)
              ? summary.metadata.device.reverseGeocoding
              : t('models.device.unknown_location', { capitalize: true })}
          </Text>
          <Text $fontSize='sm' $ellipsis>
            {completeName}
          </Text>
        </Flex>
        <div>
          <Icons.Focus
            $backgroundColor={colors.grayscale.black}
            $color={colors.grayscale.white}
            $p='md'
            $size='lg'
            onPress={focusDevice}
          />
        </div>
        <div>
          <Icons.Close
            $backgroundColor={colors.grayscale[100]}
            $p='md'
            $size='lg'
            onPress={onClose}
          />
        </div>
      </DelimitedFlex>

      {/* isSoilSensorMetric: HPOT, U_CAPA and T_SOIL_BY_HORIZON are plot-specific metrics — redirect the user instead of showing raw sensor data. */}
      {isSoilSensorMetric(focusedMetric) && isIrrigationDevice(summary.metadata.device) ? (
        <Flex
          $flexDirection='column'
          $alignItems='center'
          $justifyContent='center'
          $flex={1}
          $p='lg'
        >
          <Text $textAlign='center' $color='grayscale.700'>
            {t('models.device.actions.view_sensor_data_on_plot', { capitalize: true })}
          </Text>
        </Flex>
      ) : 'lastUpdatedAt' in summary && isNil(summary.lastUpdatedAt) ? (
        <NoEmissionEmptyState
          metrics={[focusedMetric]}
          title={t('weedget.not_emitted_yet.wait_message')}
          description={t('weedget.not_emitted_yet.waiting_for_sensor')}
        />
      ) : (
        <Box $p='md'>
          <PlotListItemMetricSummary
            metric={focusedMetric}
            data={summary.data}
            isRestricted={isRestricted}
            isVirtualDevice={
              smv ? isModelVirtualWeatherStation(summary.metadata.device.model) : false
            }
            focusedHorizon={focusedHorizon}
            reason={reason}
            timezone={summary.metadata.device.timezone}
            provider={providerName}
          />
        </Box>
      )}
      {isFrost(focusedMetric) || isSoilSensorMetric(focusedMetric) || hasNoDataForHorizon ? null : (
        <PlotCardFooter
          timestamp={lastUpdate}
          timezone={summary.metadata.device.timezone}
          isVirtualMetric={marker_redesign ? undefined : isVirtualDevice}
          warningThreshold={
            marker_redesign ? lastUpdateThresholds.warningThreshold : warningThreshold
          }
          dangerThreshold={lastUpdateThresholds.dangerThreshold}
          name={providerName}
        />
      )}
      <Link onPress={onClose} to={`/weather-map/${summary.metadata.device.id}`}>
        <DelimitedFlex
          $isDelimitedOnTop
          $p='md'
          $alignItems='center'
          $justifyContent='center'
          $gap='md'
        >
          <Text $fontWeight='medium' $color='primary.500'>
            {t('models.device.actions.view_device', { capitalize: true })}
          </Text>
          <Icons.ArrowRight $color='primary.500' $size='md' />
        </DelimitedFlex>
      </Link>
    </Card>
  )
}

export default FocusedDeviceSummaryCard
